<template>
  <v-dialog v-model="localDialog" width="854" lazy>
    <v-card>
      <v-toolbar color="primary" dark>
        <v-toolbar-title>
          {{
            event._id
              ? $tc('models.event')
              : $t('titles.global.newModel', { field: $tc('models.event') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn dark icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-layout wrap>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model.number="event.minute"
              v-validate="'required|numeric|max_value:200'"
              type="number"
              min="1"
              :max="maxMinute"
              step="1"
              maxlength="2"
              :readonly="!!event._id"
              :label="$t('fields.events.minute')"
              :data-vv-name="$t('fields.events.minute')"
              :error-messages="errors.collect($t('fields.events.minute'))"
            />
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model.number="event.second"
              v-validate="'required|numeric|max_value:59'"
              type="number"
              min="0"
              :max="maxSecond"
              step="1"
              maxlength="2"
              :readonly="!!event._id"
              :label="$t('fields.events.second')"
              :data-vv-name="$t('fields.events.second')"
              :error-messages="errors.collect($t('fields.events.second'))"
            />
          </v-flex>
          <v-flex xs12 md6 px-2>
            <v-select
              v-model="event.type"
              v-validate="'required'"
              :items="filterTypes"
              :disabled="!!event._id"
              :label="
                $t('action.actions.select', {
                  field: $tc('fields.global.type'),
                }) + ' (*)'
              "
              :data-vv-name="$tc('fields.global.type')"
              :error-messages="errors.collect($tc('fields.global.type'))"
              @change="event.typeEventId = ''"
            />
          </v-flex>
          <v-flex xs12 md6 px-2>
            <type-event-field
              v-model="event.typeEventId"
              v-validate="'required'"
              :type="event.type"
              :events="eventList"
              :disabled="!event.type || !!event._id"
              :label="$tc('models.typeEvent') + ' (*)'"
              :data-vv-name="$tc('models.typeEvent')"
              :error-messages="errors.collect($tc('models.typeEvent'))"
            />
          </v-flex>

          <v-flex xs12 md6 px-2>
            <team-field
              v-model="event.teamId"
              v-validate="{
                required:
                  event.typeEventId && event.typeEventId === ID_CHANGE_PLAYER,
              }"
              :_id="teamIds"
              :disabled="!event.typeEventId || !!event._id"
              :label="
                $t('action.actions.select', { field: $tc('models.team') })
              "
              :data-vv-name="$tc('models.team')"
              :error-messages="errors.collect($tc('models.team'))"
              @focus="(e) => e.target.click()"
              @change="event.playerId = ''"
            />
          </v-flex>
          <template
            v-if="event.typeEventId && event.typeEventId === ID_CHANGE_PLAYER"
          >
            <v-flex xs12 md6 px-2>
              <user-field
                v-model="event.incomingPlayer"
                v-validate="'required'"
                :team-id="event.teamId"
                :match-id="matchId"
                :disabled="!event.teamId || !!event._id"
                type-role="player"
                :label="$tc('fields.events.incomingPlayer')"
                :data-vv-name="$t('fields.events.incomingPlayer')"
                :error-messages="
                  errors.collect($t('fields.events.incomingPlayer'))
                "
                @focus="(e) => e.target.click()"
              />
            </v-flex>
            <v-flex xs12 md6 px-2>
              <user-field
                v-model="event.outgoingPlayer"
                v-validate="'required'"
                :team-id="event.teamId"
                :match-id="matchId"
                :disabled="!event.teamId || !!event._id"
                type-role="player"
                :label="$t('fields.events.outgoingPlayer')"
                :data-vv-name="$t('fields.events.outgoingPlayer')"
                :error-messages="
                  errors.collect($t('fields.events.outgoingPlayer'))
                "
                @focus="(e) => e.target.click()"
              />
            </v-flex>
          </template>
          <v-flex v-else xs12 md6 px-2>
            <user-field
              v-model="event.playerId"
              :team-id="event.teamId"
              :match-id="matchId"
              :disabled="!event.teamId || !!event._id"
              type-role="player"
              :label="$tc('titles.global.player')"
              @changePlayer="onChangePlayer"
              @focus="(e) => e.target.click()"
            />
          </v-flex>
          <v-flex xs12 px-2>
            <v-textarea
              v-if="event.type === 'user' || event.private"
              v-model="event.description"
              :readonly="!!event._id"
              rows="1"
              :label="$t('titles.roles.description')"
            />
          </v-flex>
          <v-flex v-if="trainingId && event.type === 'user'" md6 px-2>
            <v-switch
              v-model="event.private"
              :readonly="!!event._id"
              :label="$t('fields.events.private')"
              color="primary"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="edit && !event._id">
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
        <v-spacer />
        <meta-button type="cancel" @click="cancel" />
        <template v-if="edit && !event._id">
          <meta-button type="reset" :disabled="loading" @click="clear" />
          <meta-button
            v-if="!event._id"
            type="create"
            :loading="loading || checkingCamera"
            @click="ifSubmit(location, event.typeEventId)"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
      </v-card-actions>
    </v-card>
    <dialog-confirm-streaming
      ref="confirmStreaming"
      :text="confirmStreamingMessage"
      @submit="submit()"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import TeamField from '@/components/widgets/Input/TeamField'
import TypeEventField from '@/components/widgets/Input/TypeEventField'
import UserField from '@/components/widgets/Input/UserField'
import DialogConfirm from '@/components/widgets/dialog/DialogConfirm'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'EventForm',
  components: {
    TeamField,
    UserField,
    TypeEventField,
    DialogConfirmStreaming: DialogConfirm,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    matchId: {
      type: String,
      default: '',
      required: false,
    },
    trainingId: {
      type: String,
      default: '',
      required: false,
    },

    homeTeamId: {
      type: String,
      default: '',
      required: false,
    },
    visitorTeamId: {
      type: String,
      default: '',
      required: false,
    },
    location: {
      type: String,
      default: '',
      required: false,
    },
    maxSecond: {
      type: Number,
      default: 59,
      required: false,
    },
    maxMinute: {
      type: Number,
      default: 59,
      required: false,
    },
  },
  data() {
    return {
      event: {},
      confirmStreamingMessage: '',
      checkingCamera: false,
    }
  },
  watch: {
    selected: {
      handler: function (val) {
        if (val) this.event = parseObject(val)
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.event = parseObject(this.selected)
    this.fetchListConst()
  },
  computed: {
    ...mapState({
      selected: (state) => state.matchEvents.selected,
      loading: (state) => state.matchEvents.loading,
      typeEventsConst: (state) => state.typeEvents.typeEventsConst,
    }),
    ...mapGetters({
      typeEventsSelectList: 'typeEvents/selectList',
      types: 'typeEvents/types',
      eventList: 'matchEvents/formatedList',
    }),
    localDialog: {
      get() {
        return this.dialog
      },
      set(value) {
        if (!value) this.cancel()
      },
    },
    filterTypes() {
      if (this.matchId) {
        return this.types.filter((item) => item.value !== 'training')
      } else if (this.trainingId) {
        return this.types.filter((item) => item.value !== 'official')
      } else return []
    },
    teamIds() {
      let teamIds = []
      if (this.homeTeamId) teamIds.push(this.homeTeamId)
      if (this.visitorTeamId) teamIds.push(this.visitorTeamId)
      if (teamIds.length) return teamIds
      else return null
    },
    ID_CHANGE_PLAYER() {
      return this.typeEventsConst && this.typeEventsConst.ID_CHANGE_PLAYER
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'matchEvents/create',
      setUpdate: 'matchEvents/update',
      reset: 'matchEvents/reset',
      uploadFile: 'files/uploadFile',
      fetchListConst: 'typeEvents/fetchListConst',
      isCameraInUse: 'pitches/isCameraInUse',
    }),
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.event)
        if (this.matchId) payload.matchId = this.matchId
        else if (this.trainingId) payload.trainingId = this.trainingId
        if (!payload._id) {
          this.setCreate(payload).then(() => {
            this.cancel()
          })
        } else {
          this.setUpdate(payload).then(() => {
            this.cancel()
          })
        }
      })
    },
    ifSubmit(pitchId, typeEventId) {
      if (pitchId && typeEventId == this.typeEventsConst.ID_START_STREAMING) {
        this.checkingCamera = true
        this.isCameraInUse(this.location)
          .then(({ status, inUse }) => {
            this.confirmStreamingMessage = ''
            if (status === false) {
              this.confirmStreamingMessage =
                this.$t('titles.global.cameraOffline') + ' '
            } else if (inUse) {
              this.confirmStreamingMessage =
                this.$t('titles.global.cameraInUse') + ' '
            }
            if (this.confirmStreamingMessage) {
              this.confirmStreamingMessage += this.$t(
                'titles.global.confirmMessage',
                {
                  field:
                    `<span class="text-uppercase primary--text">` +
                    this.$t('action.buttons.startStreaming') +
                    '</span>',
                }
              )
              this.$refs.confirmStreaming.show()
            } else {
              this.submit()
            }
          })
          .catch(() => {
            this.submit()
          })
          .finally(() => {
            this.checkingCamera = false
          })
      } else {
        this.submit()
      }
    },
    onChangePlayer(player) {
      this.event.playerDorsal = player.dorsal
    },
    clear() {
      this.$nextTick(() => {
        this.reset()
        this.$validator.reset()
      })
    },
    cancel() {
      this.$emit('update:dialog', false)
      this.clear()
    },
  },
}
</script>
